import { useSelector, shallowEqual } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { selectors as meterTypeSelectors } from 'state/meter-type/meter-type-slice';
import { selectors as meterpointValidationSelectors } from 'state/meterpoint-validation/meterpoint-validation-slice';
import {
  METER_TYPE_ERROR_CODES,
  UNSPPORTED_METER_ERRORS,
  UNSPPORTED_METER_ERRORS_LEGACY
} from 'config/app-config';
import { selectFuelType } from 'state/fuel-type/fuel-type-slice';
import { TMeter, TMeterError } from 'api/models/TMeter';

const includesErrorCodes = (errors, errorCodeList) =>
  errors.some(({ code }) => errorCodeList.includes(code));

export type TUseMeterType = {
  electricityMeter?: TMeter;
  electricityMeters?: TMeter[];
  gasMeter?: TMeter;
  hasElectricityMeter: boolean;
  hasGasMeter: boolean;
  hasE7Meter: boolean;
  hasElectricityTOUMeter?: boolean;
  hasAnyPrepayMeter?: boolean;
  hasAnyTOUMeter?: boolean;
  hasElectricityPrepayMeter?: boolean;
  hasRelatedElectricityMeters?: boolean;
  hasGasPrepayMeter?: boolean;
  isGasPrepayMeter?: boolean;
  isElecPrepayMeter?: boolean;
  hasGasTOUMeter?: boolean;
  isUnsupportedElectricityMeter: boolean;
  isEvException?: boolean;
  isUneligibleElectricityMeter?: boolean;
  isUnsupportedGasMeter: boolean;
  isUneligibleGasMeter?: boolean;
  meterTypeErrors: TMeterError[];
  allMeterErrors?: TMeterError[];
  electricityErrors?: TMeterError[];
  gasErrors?: TMeterError[];
  isBGECustomer?: boolean;
  isMeterpointValidationRequired?: boolean;
  isBGCustomer?: boolean;
  selectedFuelErrors: TMeterError[];
  isBGCustomerForElec?: boolean;
  isBGECustomerForElec: boolean;
  isBGECustomerForGas: boolean;
  isBGCustomerForGas?: boolean;
  isRelatedElectricityMeter: boolean;
  regionCode?: string;
};

export const useMeterSelectors = () => {
  const {
    selectValidationRequired,
    selectUserMeterpoints
  } = meterpointValidationSelectors;
  const meterpointValidationRequired = useSelector(
    selectValidationRequired,
    shallowEqual
  );
  const { MPAN, MPRN } = useSelector(selectUserMeterpoints, shallowEqual);
  const isMeterpointValidationRequired = Object.values(
    meterpointValidationRequired
  ).some((v) => v);

  const electricityMeterSelectors = MPAN
    ? meterpointValidationSelectors
    : meterTypeSelectors;
  const gasMeterSelectors = MPRN
    ? meterpointValidationSelectors
    : meterTypeSelectors;
  const commonSelectors =
    MPAN || MPRN ? meterpointValidationSelectors : meterTypeSelectors;

  const {
    selectElectricityMeter,
    selectHasE7Meter,
    selectHasElectricityTOUMeter,
    selectHasElectricityPrepayMeter,
    selectElectricityErrors,
    selectIsRelatedElectricityMeterDeprecated,
    selectHasRelatedElectricityMeters,
    selectElectricityMeters
  } = electricityMeterSelectors;

  const {
    selectGasMeter,
    selectGasErrors,
    selectHasGasPrepayMeter,
    selectHasGasTOUMeter
  } = gasMeterSelectors;

  const {
    selectErrors,
    selectIsBGECustomer,
    selectIsBGCustomer
  } = commonSelectors;

  return {
    selectElectricityMeter,
    selectHasE7Meter,
    selectHasElectricityTOUMeter,
    selectHasElectricityPrepayMeter,
    selectElectricityErrors,
    selectIsRelatedElectricityMeterDeprecated,
    selectHasRelatedElectricityMeters,
    selectElectricityMeters,
    selectGasMeter,
    selectHasGasPrepayMeter,
    selectHasGasTOUMeter,
    selectGasErrors,
    isMeterpointValidationRequired,
    selectErrors,
    selectIsBGECustomer,
    selectIsBGCustomer
  };
};

export const useMeterType = (): TUseMeterType => {
  const {
    selectElectricityMeter,
    selectHasE7Meter,
    selectHasElectricityTOUMeter,
    selectHasElectricityPrepayMeter,
    selectElectricityErrors,
    selectIsRelatedElectricityMeterDeprecated,
    selectHasRelatedElectricityMeters,
    selectElectricityMeters,
    selectGasMeter,
    selectHasGasPrepayMeter,
    selectHasGasTOUMeter,
    selectGasErrors,
    isMeterpointValidationRequired,
    selectIsBGECustomer,
    selectIsBGCustomer
  } = useMeterSelectors();
  const { releaseRelatedMeterAcquisition } = useFlags();
  const unsupportedMeterErrors = releaseRelatedMeterAcquisition
    ? UNSPPORTED_METER_ERRORS
    : UNSPPORTED_METER_ERRORS_LEGACY;
  const fuelType = useSelector(selectFuelType, shallowEqual);

  const UNELIGIBLE_METER_ERRORS = [METER_TYPE_ERROR_CODES.MeterEVException];

  const electricityMeters = useSelector(selectElectricityMeters, shallowEqual);
  const hasRelatedElectricityMeters = useSelector(
    selectHasRelatedElectricityMeters,
    shallowEqual
  );
  const electricityMeter = useSelector(selectElectricityMeter, shallowEqual);
  const isRelatedElectricityMeter = useSelector(
    selectIsRelatedElectricityMeterDeprecated,
    shallowEqual
  );
  const gasMeter = useSelector(selectGasMeter, shallowEqual);
  const isElecPrepayMeter = useSelector(
    selectHasElectricityPrepayMeter,
    shallowEqual
  );
  const isGasPrepayMeter = useSelector(selectHasGasPrepayMeter, shallowEqual);

  const hasElectricityPrepayMeter = fuelType.electric && isElecPrepayMeter;
  const hasGasPrepayMeter = fuelType.gas && isGasPrepayMeter;

  const hasAnyPrepayMeter = hasElectricityPrepayMeter || hasGasPrepayMeter;

  const hasElectricityMeter = !!electricityMeter;
  const electricityErrors = useSelector(selectElectricityErrors, shallowEqual);
  const isUnsupportedElectricityMeter = includesErrorCodes(
    electricityErrors,
    unsupportedMeterErrors
  );
  const isUneligibleElectricityMeter = includesErrorCodes(
    electricityErrors,
    UNELIGIBLE_METER_ERRORS
  );
  const isEvException = includesErrorCodes(electricityErrors, [
    METER_TYPE_ERROR_CODES.MeterEVException
  ]);

  const hasElecE7Meter = useSelector(selectHasE7Meter, shallowEqual);
  const hasE7Meter = !!(fuelType.electric && hasElecE7Meter); //For gas fuel cannot have two rate meter
  const isElecTOUMeter = useSelector(
    selectHasElectricityTOUMeter,
    shallowEqual
  );
  const isGasTOUMeter = useSelector(selectHasGasTOUMeter, shallowEqual);
  const hasElectricityTOUMeter = fuelType.electric && isElecTOUMeter;
  const hasGasTOUMeter = fuelType.gas && isGasTOUMeter;
  const hasAnyTOUMeter = hasElectricityTOUMeter || hasGasTOUMeter;

  const hasGasMeter = !!gasMeter;
  const gasErrors = useSelector(selectGasErrors, shallowEqual);
  const isUnsupportedGasMeter = includesErrorCodes(
    gasErrors,
    unsupportedMeterErrors
  );
  const isUneligibleGasMeter = includesErrorCodes(
    gasErrors,
    UNELIGIBLE_METER_ERRORS
  );

  const isBGECustomer = useSelector(selectIsBGECustomer, shallowEqual);
  const isBGCustomer = useSelector(selectIsBGCustomer, shallowEqual);
  const isBGCustomerForGas = includesErrorCodes(gasErrors, [
    METER_TYPE_ERROR_CODES.MeterCurrentSupplierBGREException
  ]);
  const isBGCustomerForElec = includesErrorCodes(electricityErrors, [
    METER_TYPE_ERROR_CODES.MeterCurrentSupplierBGREException
  ]);
  const isBGECustomerForGas = includesErrorCodes(gasErrors, [
    METER_TYPE_ERROR_CODES.MeterCurrentSupplierNEPException
  ]);
  const isBGECustomerForElec = includesErrorCodes(electricityErrors, [
    METER_TYPE_ERROR_CODES.MeterCurrentSupplierNEPException
  ]);

  const allMeterErrors = [...electricityErrors, ...gasErrors];
  const meterTypeErrors = allMeterErrors.filter(({ code }) =>
    unsupportedMeterErrors.includes(code)
  );

  const selectedFuelErrors = [
    ...(fuelType.gas ? gasErrors : []),
    ...(fuelType.electric ? electricityErrors : [])
  ].filter(({ code }) => unsupportedMeterErrors.includes(code));

  const regionCode = electricityMeter?.regionCode;

  return {
    electricityMeter,
    electricityMeters,
    gasMeter,

    hasElectricityMeter,
    hasGasMeter,
    hasE7Meter,
    hasElectricityTOUMeter,
    hasAnyPrepayMeter,
    hasAnyTOUMeter,
    hasElectricityPrepayMeter,
    hasRelatedElectricityMeters,
    hasGasPrepayMeter,
    isGasPrepayMeter,
    isElecPrepayMeter,
    hasGasTOUMeter,
    isUnsupportedElectricityMeter,
    isEvException,
    isUneligibleElectricityMeter,
    isUnsupportedGasMeter,
    isUneligibleGasMeter,
    meterTypeErrors,
    allMeterErrors,
    electricityErrors,
    gasErrors,
    isBGECustomer,
    isMeterpointValidationRequired,
    isBGCustomer,
    selectedFuelErrors,
    isBGCustomerForElec,
    isBGECustomerForElec,
    isBGECustomerForGas,
    isBGCustomerForGas,
    isRelatedElectricityMeter,
    regionCode
  };
};
